if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        const requestedEditLine = new URLSearchParams(window.location.search).get('edit_line');
        const productUrl = this.closest('product-info')?.dataset.url;
        const isCurrentProduct = productUrl
          ? new URL(productUrl, window.location.origin).pathname === window.location.pathname
          : false;
        this.editLineKey = isCurrentProduct ? requestedEditLine : null;
        this.editLinePromise = this.editLineKey ? this.loadCartLineEdit() : null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formId = this.form.getAttribute('id');
        const productInfo = this.closest('product-info') || (formId ? document.querySelector(`product-info[data-section="${this.dataset.sectionId}"]`) : null) || document;
        const boardingPers = productInfo.querySelector('boarding-personalisation, santa-personalisation, colouring-personalisation, label-personalisation, envelope-name-input');
        if (boardingPers && typeof boardingPers.validate === 'function') {
          const validationError = boardingPers.validate();
          if (validationError) {
            this.handleErrorMessage(validationError);
            this.dispatchCartErrorEvent(validationError, 'INVALID');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.disabled = false;
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }
        }
        const hasFiles = boardingPers && typeof boardingPers.hasFiles === 'function' && boardingPers.hasFiles();
        const isMultiCardBoarding = boardingPers && typeof boardingPers.getCartItems === 'function' && boardingPers.cards.length > 0 && !hasFiles;

        let cartRequest;
        let variantId;
        let quantity;
        let linesUpdateDeferred;

        if (isMultiCardBoarding && !this.editLinePromise) {
          let items;
          try {
            items = boardingPers.getCartItems();
          } catch (error) {
            this.handleErrorMessage(error.message);
            this.dispatchCartErrorEvent(error.message, 'INVALID');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }
          const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
          const payload = {
            items,
            sections,
            sections_url: window.location.pathname,
          };

          if (this.cart) {
            this.cart.setActiveElement(document.activeElement);
          }

          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);

          variantId = items[0]?.id;
          quantity = items.length;
          linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
          cartRequest = fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        } else {
          this.closest('product-info')?.querySelector('media-gallery')?.syncLayeredPreviewCartProperties?.();
          const formData = new FormData(this.form);

          const associatedElements = formId ? document.querySelectorAll(`[form="${formId}"]`) : [];
          associatedElements.forEach((el) => {
            if (!el.name || el.disabled) return;
            if (el.type === 'checkbox' || el.type === 'radio') {
              if (el.checked) formData.set(el.name, el.value);
            } else if (el.type === 'file') {
              if (el.files && el.files.length > 0) {
                formData.set(el.name, el.files[0]);
              }
            } else if (el.value !== undefined && el.value !== '') {
              formData.set(el.name, el.value);
            }
          });

          const qtyInput = productInfo.querySelector('.quantity__input') || (formId ? document.querySelector(`[form="${formId}"][name="quantity"]`) : null);
          const resolvedQuantity = (qtyInput && parseInt(qtyInput.value, 10)) || parseInt(formData.get('quantity'), 10) || 1;
          formData.set('quantity', String(resolvedQuantity));

          if (boardingPers && typeof boardingPers.getCartItems === 'function') {
            const firstItem = boardingPers.getCartItems()?.[0];
            if (firstItem && firstItem.id) {
              formData.set('id', String(firstItem.id));
              const formIdInput = this.form.querySelector('input[name="id"]');
              if (formIdInput) formIdInput.value = String(firstItem.id);
            }
          }

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          variantId = formData.get('id');
          quantity = resolvedQuantity;
          linesUpdateDeferred = this.editLinePromise
            ? null
            : this.createCartLinesUpdateEvent(variantId, quantity);
          cartRequest = this.editLinePromise
            ? this.submitCartLineEdit(formData, config, variantId, quantity)
            : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        }

        cartRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.disabled = false;
            } else {
              this.submitButton.disabled = false;
            }
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      loadCartLineEdit() {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error('Unable to load the cart item');
            return response.json();
          })
          .then((cart) => {
            const line = cart.items?.find((item) => item.key === this.editLineKey);
            const productInfo = this.closest('product-info');
            const productPath = productInfo?.dataset.url
              ? new URL(productInfo.dataset.url, window.location.origin).pathname
              : window.location.pathname;
            const linePath = line?.url ? new URL(line.url, window.location.origin).pathname : null;

            if (!line || linePath !== productPath) throw new Error('The cart item is no longer available');

            this.editLine = line;
            this.restoreCartLineEdit(line);
            return line;
          })
          .catch((error) => {
            console.error(error);
            this.clearCartLineEdit();
            return null;
          });
      }

      restoreCartLineEdit(line) {
        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput && 'value' in quantityInput) {
          const currentMaximum = parseInt(quantityInput.max, 10);
          if (!Number.isNaN(currentMaximum)) quantityInput.max = String(currentMaximum + line.quantity);
          quantityInput.value = String(line.quantity);
        }

        const properties = line.properties || {};
        Array.from(this.form.elements).forEach((input) => {
          const propertyMatch = input.name?.match(/^properties\[(.+)\]$/);
          if (!propertyMatch || input.type === 'file') return;

          const value = properties[propertyMatch[1]];
          if (value == null) return;

          if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = String(input.value) === String(value);
          } else {
            input.value = value;
          }
        });
      }

      async submitCartLineEdit(formData, addConfig, variantId, quantity) {
        const line = await this.editLinePromise;
        if (!line) return fetch(`${routes.cart_add_url}`, addConfig).then((response) => response.json());

        const propertyState = this.getSubmittedProperties(formData, line);
        const boardingPers = this.closest('product-info')?.querySelector('boarding-personalisation, santa-personalisation');
        if (typeof boardingPers?.cleanCartProperties === 'function') {
          propertyState.properties = boardingPers.cleanCartProperties(propertyState.properties);
        }
        const originalProperties = line.properties || {};
        const variantChanged = String(variantId) !== String(line.variant_id);
        const removesAllProperties =
          Object.keys(originalProperties).length > 0 && Object.keys(propertyState.properties).length === 0;
        const requiresReplacement = variantChanged || propertyState.hasFiles || removesAllProperties;

        if (!requiresReplacement) {
          const changePayload = {
            id: line.key,
            quantity,
            ...this.getCartSectionPayload(),
          };

          if (Object.keys(propertyState.properties).length > 0) {
            changePayload.properties = propertyState.properties;
          }

          const response = await this.requestCartChange(changePayload);
          if (!this.hasCartError(response)) {
            response.id = variantId;
            this.clearCartLineEdit();
          }
          return response;
        }

        if (!propertyState.hasFiles) this.replaceFormDataProperties(formData, propertyState.properties);
        addConfig.body = formData;

        const addedLine = await fetch(`${routes.cart_add_url}`, addConfig).then((response) => response.json());
        if (this.hasCartError(addedLine)) return addedLine;

        let updatedCart;
        try {
          updatedCart = await this.requestCartChange({
            id: line.key,
            quantity: 0,
            ...this.getCartSectionPayload(),
          });
        } catch (error) {
          await this.rollbackAddedLine(addedLine, quantity);
          throw error;
        }

        if (this.hasCartError(updatedCart)) {
          await this.rollbackAddedLine(addedLine, quantity);
          return updatedCart;
        }

        updatedCart.id = variantId;
        updatedCart.key = addedLine.key;
        this.clearCartLineEdit();
        return updatedCart;
      }

      getSubmittedProperties(formData, line) {
        const properties = { ...(line.properties || {}) };
        let hasFiles = false;

        for (const [name, value] of formData.entries()) {
          const propertyMatch = name.match(/^properties\[(.+)\]$/);
          if (!propertyMatch) continue;

          const propertyName = propertyMatch[1];
          if (typeof File !== 'undefined' && value instanceof File) {
            if (value.size > 0) hasFiles = true;
            continue;
          }

          if (String(value).length > 0) {
            properties[propertyName] = value;
          } else {
            delete properties[propertyName];
          }
        }

        return { properties, hasFiles };
      }

      replaceFormDataProperties(formData, properties) {
        const propertyFields = new Set(
          Array.from(formData.keys()).filter((name) => /^properties\[(.+)\]$/.test(name))
        );
        propertyFields.forEach((name) => formData.delete(name));
        Object.entries(properties).forEach(([name, value]) => formData.append(`properties[${name}]`, value));
      }

      getCartSectionPayload() {
        if (!this.cart) return {};

        return {
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      async requestCartChange(payload) {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (!response.ok && !data.status) data.status = response.status;
        if (data.errors && !data.description) {
          data.status = data.status || response.status || 422;
          data.description = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
        }

        return data;
      }

      async rollbackAddedLine(addedLine, addedQuantity) {
        const totalQuantity = parseInt(addedLine.quantity, 10);
        if (!addedLine.key || Number.isNaN(totalQuantity)) return;

        try {
          await this.requestCartChange({
            id: addedLine.key,
            quantity: Math.max(0, totalQuantity - addedQuantity),
          });
        } catch (error) {
          console.error(error);
        }
      }

      hasCartError(response) {
        return !response || Boolean(response.status || response.errors);
      }

      clearCartLineEdit() {
        this.editLineKey = null;
        this.editLine = null;
        this.editLinePromise = null;

        const url = new URL(window.location.href);
        url.searchParams.delete('edit_line');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
